import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  PLATFORM_ID,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {AbstractControl, AsyncValidatorFn, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {DcpValidators} from '@app/services/dcp-validators';
import {DcpCustomerRegistrationDecoratorService} from '@app/services/dcp-customer-registration-decorator.service';
import {HttpErrorResponse} from '@angular/common/http';
import {finalize, first, mergeMap, switchMap, take} from 'rxjs/operators';
import {B2BUser, TranslationService} from '@spartacus/core';
import {combineLatest, Observable, of, Subject} from 'rxjs';
import {AddressUsage} from '@app/model/order/dcp-order-entry.model';
import {FeatureTogglesService} from '@dcp/configurator';
import {UNLEASH_TOGGLES} from '@app/config/FeatureToggleConfig';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {DefaultModalService} from '@app/shared/modal/services/default-modal.service';

enum ContactTypes { Individual = 'individual', Department = 'department' }

@UntilDestroy()
@Component({
  selector: 'dcp-technical-contacts-add-modal',
  templateUrl: './technical-contacts-add-modal.component.html'
})
export class TechnicalContactsAddModalComponent implements OnInit {
  @Input()
  header: string;
  @Input()
  submitButtonText: string;
  @Input()
  openModal$: Subject<void>;
  @Input()
  isDepartmentToggleEnabled: boolean = true;
  @Input()
  contactRole: AddressUsage;
  private _contactToEdit: B2BUser;
  @Input()
  set contactToEdit(contact: B2BUser) {
    this._contactToEdit = contact;
    this.setForm(this._contactToEdit);
  }

  @Input() contactType: string;
  @Input() isEditModal: boolean = false;
  @Output()
  isModalOpenedChange = new EventEmitter<boolean>();
  @Output()
  newContactCreated = new EventEmitter<object>();
  @Output() contactEdited = new EventEmitter<B2BUser>();
  @Output() contactUpdated = new EventEmitter<B2BUser>();
  @ViewChild('contractType', {read: ElementRef, static: false}) contractTypeRef: ElementRef;
  @ViewChild('modalContent') modalContent: TemplateRef<any>;

  showErrorMessages = false;
  isConsentCheckboxChecked = false;
  topModalId: string;
  error: HttpErrorResponse;
  isLoading = false;
  readonly contactTypes = ContactTypes;
  selectedType: ContactTypes = ContactTypes.Individual;
  public errorMessages = {
    firstName: 'registration.error.noError',
    lastName: 'registration.error.noError',
    email: 'registration.error.noError'
  };
  form: UntypedFormGroup = this.formBuilder.group({
      firstName: this.formBuilder.control(
        '',
        [
          Validators.required,
          Validators.maxLength(40),
          DcpValidators.onlyLatin1Characters(),
          this.dcpValidators.noForbiddenWords(),
          DcpValidators.atLeastOneLetter(),
        ]
      ),
      lastName: this.formBuilder.control(
        '',
      ),
      email: this.formBuilder.control(
        '',
        [
          Validators.required,
          Validators.email,
          Validators.maxLength(60),
        ],
        [
          this.sameDomainValidator(),
        ]
      ),
    },
    {
      validators: [
        DcpValidators.checkMatch('firstName', 'lastName'),
      ]
    }
  );

  constructor(
    private formBuilder: UntypedFormBuilder,
    private dcpValidators: DcpValidators,
    private registrationService: DcpCustomerRegistrationDecoratorService,
    private cdr: ChangeDetectorRef,
    private translationService: TranslationService,
    private featureTogglesService: FeatureTogglesService,
    private modalService: DefaultModalService,
    @Inject(PLATFORM_ID) public platformId: string,
  ) {
  }

  ngOnInit(): void {
    this.onTypeChange(this._contactToEdit?.titleCode === '0003' ? [ContactTypes.Department] : [ContactTypes.Individual]);
    this.openModal$.pipe(
      untilDestroyed(this)
    ).subscribe(() => this.openModal());
    this.modalService.onClose().pipe(
      untilDestroyed(this)
    ).subscribe(() => {
      if (!this.isEditModal) {
        this.resetForm();
        this.resetErrorMessages();
      }
    });
  }

  sameDomainValidator(): AsyncValidatorFn | undefined {
    return ({value}: AbstractControl) => this.featureTogglesService.isEnabled(UNLEASH_TOGGLES.disableEndContactDomainCheck).pipe(
      take(1),
      switchMap((dontCheckDomain) => dontCheckDomain ? of(null) : this.dcpValidators.sameDomainImpl(value)));
  }


  closeModal(): void {
    this.modalService.closeModal(this.topModalId);
  }

  onSubmit(): void {
    this.error = null;
    if (!this.isConsentCheckboxChecked || this.form.invalid) {
      this.setShowErrorMessages(true);
      return;
    }
    this.isLoading = true;

    this.trimInput('firstName');
    this.trimInput('lastName');

    const {firstName, lastName, email} = this.form.controls;
    const isDepartment = this.selectedType === 'department';
    if (this._contactToEdit) {
      this._contactToEdit.firstName = firstName.value;
      this._contactToEdit.lastName = lastName.value;
      this._contactToEdit.email = email.value;

      this.registrationService.updateContact(this._contactToEdit, isDepartment).pipe(first(),
        finalize(() => {
          this.isLoading = false;
          this.cdr.detectChanges();
        }),
        untilDestroyed(this)
      ).subscribe((contact: B2BUser) => {
          this.contactUpdated.emit(contact);
          this.closeModal();
        },
        (error: HttpErrorResponse) => {
          this.error = error;
        });
    } else {
      this.registrationService.registerNewContact(firstName.value, lastName.value, email.value, this.contactRole, isDepartment).pipe(untilDestroyed(this),
        finalize(() => {
          this.isLoading = false;
          this.cdr.detectChanges();
        })
      ).subscribe((contact: B2BUser) => {
          this.newContactCreated.emit(contact);
          this.closeModal();
        },
        (error: HttpErrorResponse) => {
          this.error = error;
        },
      );
    }
  }

  onInputChange(event: CustomEvent, field: string): void {
    const control = this.getFormControl(field);

    const target = event.target as HTMLInputElement;
    control.setValue(target.value);
    this.setErrorMessage(field);
  }

  onSelect(event: CustomEvent): void {
    this.onTypeChange([event.detail.item.value]);
  }

  onCheckboxChange(value: boolean): void {
    this.isConsentCheckboxChecked = !value;
  }

  onTypeChange([type]: string[]): void {
    this.selectedType = type as ContactTypes;
    const isDepartment = (this.selectedType === ContactTypes.Department);
    if (this._contactToEdit) {
      this.form.get('firstName').setValue(isDepartment ? 'Departments' : this._contactToEdit.firstName);
      this.form.get('lastName').setValue(isDepartment ? this._contactToEdit.name : this._contactToEdit.lastName);
    } else {
      this.form.get('firstName').setValue(isDepartment ? 'Departments' : '');
      this.form.get('lastName').setValue('');
    }
    this.setValidatorsForLastName(isDepartment);
    this.showErrorMessages = false;
  }

  openModal(): void {
    if (this.modalContent) {
      this.topModalId = this.modalService.openModal(this.modalContent, this.header, undefined, {customClasses: ['checkout-billing-contact']});
    }
  }

  setValidatorsForLastName(isDepartment: boolean) {
    const validators = [
      Validators.required,
      Validators.maxLength(40),
      DcpValidators.onlyLatin1Characters(),
      DcpValidators.atLeastOneLetter(),
    ];
    if (!isDepartment) {
      validators.push(this.dcpValidators.noForbiddenWords());
    }
    this.form.get('lastName').setValidators(validators);
    this.form.get('lastName').updateValueAndValidity();
  }

  typeOptionLabel(type: ContactTypes): Observable<string> {
    return combineLatest([
      this.translationService.translate(`checkout.digitalNativeCheckout.newContact.${type}`),
      this.translationService.translate(this.contactType),
    ]).pipe(
      take(1),
      mergeMap(([typeTranslated, headerTranslated]) => this.translationService.translate(
        'checkout.digitalNativeCheckout.newContact.typeOption',
        {type: typeTranslated, contact: headerTranslated.toLowerCase()}
      )),
    );
  }

  lastNameLabel(): Observable<string> {
    const field = {
      [ContactTypes.Individual]: 'lastName',
      [ContactTypes.Department]: 'department'
    }[this.selectedType];
    return this.translationService.translate(`registration.label.${field}`);
  }

  trimInput(field: string) {
    const currentValue = this.form.controls[field].value;
    this.form.controls[field].patchValue(currentValue.trim());
  }

  hasFieldError(field: string, errorType?: string): boolean {
    const hasError = this.showErrorMessages && this.form.get(field)?.errors;
    return errorType ? hasError && this.form.get(field)?.errors[errorType] : hasError;
  }

  hasMatchError(field1: string, field2: string): boolean {
    const matchErrors = [`checkMatch-${field1}:${field2}`, `checkMatch-${field2}:${field1}`];
    return this.showErrorMessages
      && !this.form.get(field1)?.errors
      && Object.keys(this.form.errors || {}).some(err => matchErrors.includes(err));
  }

  isNeedToShowContactType(): boolean {
    return !this._contactToEdit;
  }

  setShowErrorMessages(state: boolean): void {
    this.showErrorMessages = state;
    this.validateAllInputs();
  }

  getErrorStatus(field: string): string {
    return this.showErrorMessages
    && (Object.values(this.form.errors || {}).some(({field1, field2}) => field1 === field || field2 === field)
      || this.hasFieldError(field))
      ? 'Error' : 'Standard';
  }

  setErrorMessage(field: string): void {
    const formField = this.getFormControl(field);
    const errors = formField?.errors || {};
    let error = 'registration.error.noError';
    const matchValidatorError = this.getMatchValidationError(field);
    if (matchValidatorError || this.hasFieldError(field)) {
      const errorName = this.fieldNameParser(Object.keys(errors)[0]);
      error = `registration.error.${matchValidatorError || errorName}`;
    }
    this.errorMessages[field] = error;
  }

  getFormControl(field: string): AbstractControl {
    return this.form.get(field);
  }

  getErrorMessage(field: string): string {
    return this.errorMessages[field];
  }

  isMaxLengthError(field: string): boolean {
    return this.getErrorMessage(field) === 'registration.error.maxLength';
  }

  getMatchValidationError(field: string): string {
    if (field === 'firstName' && this.hasMatchError(field, 'lastName')) {
      return 'duplicatesNames2'
    }

    if (field === 'lastName' && this.hasMatchError(field, 'firstName')) {
      return 'duplicatesNames'
    }

    return ''
  }

  getCheckboxValueState(): string {
    return this.showErrorMessages && !this.isConsentCheckboxChecked ? 'Error' : 'Standard';
  }

  getMaxLength(field: string): number {
    return field === 'email' ? 60 : 40;
  }

  validateAllInputs(): void {
    if (this.showErrorMessages) {
      Object.keys(this.errorMessages).forEach(field => this.setErrorMessage(field));
    }
  }

  resetForm(): void {
    this.form.reset({firstName: '', lastName: '', email: ''});
    this.isConsentCheckboxChecked = false;
  }

  resetErrorMessages(): void {
    this.errorMessages = {
      firstName: 'registration.error.noError',
      lastName: 'registration.error.noError',
      email: 'registration.error.noError'
    };
    this.showErrorMessages = false;
  }

  private fieldNameParser(field: string): string {
    switch (field) {
      case 'maxlength':
        return 'maxLength';
      case 'onlyLatin1Characters':
        return 'nonLatin';
      case 'atLeastOneLetter':
        return 'oneLetter';
      default:
        return field;
    }
  }

  private setForm(contact: B2BUser) {
    if (contact) {
      this.form.get('firstName').patchValue(contact.firstName);
      this.form.get('lastName').patchValue(contact.lastName);
      this.form.get('email').patchValue(contact.email);
    }
  }
}
